import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Snackbar,
  Alert,
  Box,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { License } from '../types/license';
import { LicenseDetailDialog } from '../components/LicenseDetailDialog';
import client from '../api/client';

// ユーザー専用ライセンス取得API
const getMyLicenses = async (): Promise<License[]> => {
  const response = await client.get('/api/my-licenses');
  return response.data;
};

const MyLicensesPage: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const { data: licenses, isLoading, error } = useQuery({
    queryKey: ['my-licenses'],
    queryFn: getMyLicenses,
    retry: false
  });

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'ACTIVE':
        return 'success';
      case 'INACTIVE':
        return 'warning';
      case 'EXPIRED':
        return 'error';
      case 'REVOKED':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleLicenseClick = (license: License) => {
    setSelectedLicense(license);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedLicense(null);
  };

  const handleSnackbarClose = () => {
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          マイライセンス
        </Typography>
        <Typography>読み込み中...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          マイライセンス
        </Typography>
        <Card>
          <CardContent>
            <Typography color="error">
              ライセンス情報の取得に失敗しました。バックエンドAPIの実装が必要です。
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              ユーザー専用のライセンス取得エンドポイント（/api/my-licenses）がまだ実装されていません。
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        マイライセンス
      </Typography>

      <Typography variant="body1" color="text.secondary" gutterBottom>
        あなたが使用可能なライセンスの一覧です。
      </Typography>

      {/* ライセンス統計情報 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                総ライセンス数
              </Typography>
              <Typography variant="h4">
                {licenses?.length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                アクティブ
              </Typography>
              <Typography variant="h4" color="success.main">
                {licenses?.filter(l => l.status === 'ACTIVE' && (l.currentActivations || 0) > 0).length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                非アクティブ
              </Typography>
              <Typography variant="h4" color="warning.main">
                {licenses?.filter(l => l.status === 'ACTIVE' && (l.currentActivations || 0) === 0).length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                期限切れ/無効
              </Typography>
              <Typography variant="h4" color="error.main">
                {licenses?.filter(l => l.status === 'EXPIRED' || l.status === 'REVOKED').length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ライセンス一覧テーブル */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ライセンスキー</TableCell>
                <TableCell>ステータス</TableCell>
                <TableCell>アクティベーション数</TableCell>
                <TableCell>最大アクティベーション数</TableCell>
                <TableCell>作成日</TableCell>
                <TableCell>有効期限</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {licenses && licenses.length > 0 ? (
                licenses.map((license) => (
                  <TableRow
                    key={license.id}
                    hover
                    onClick={() => handleLicenseClick(license)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {license.key.substring(0, 16)}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={license.status}
                        color={getStatusColor(license.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{license.currentActivations || 0}</TableCell>
                    <TableCell>{license.maxActivations || 1}</TableCell>
                    <TableCell>
                      {license.createdAt ? new Date(license.createdAt).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      {license.expiresAt ? new Date(license.expiresAt).toLocaleDateString() : '無期限'}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary">
                      ライセンスがありません
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ライセンス詳細ダイアログ */}
      {selectedLicense && (
        <LicenseDetailDialog
          license={selectedLicense}
          open={detailDialogOpen}
          onClose={handleCloseDetailDialog}
          isUserView={true} // ユーザービュー専用フラグ
        />
      )}

      {/* 成功/エラーメッセージ */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MyLicensesPage;